import { transformUrnToWitboostId } from '@agilelab/plugin-wb-builder-common';
import {
  Pagination,
  PermissionSettingsView,
  PermissionViewFilters,
} from '@agilelab/plugin-wb-rbac-common';
import { parseEntityRef } from '@mainstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import useAsync from 'react-use/lib/useAsync';
import { getEntitiesDisplayNames } from './enrichDetails';

export interface PermissionContextType {
  loading: boolean;
  filters: PermissionViewFilters;
  changeFilters: <K extends keyof PermissionViewFilters>(
    key: K,
    value: PermissionViewFilters[K],
  ) => void;
  pagination: Pagination;
  setPagination: (p: Pagination) => void;
  permissions: PermissionSettingsView[];
  permissionsCount: number;
  reloadTable: () => void;
}

export const PermissionContext = createContext<PermissionContextType>(
  {} as PermissionContextType,
);

type Props = {
  children?: ReactNode;
};

export const PermissionContextProvider: FC<Props> = ({ children }) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [loading, setLoading] = useState(true);
  const [permissionsTotal, setPermissionsTotal] = useState<number>(0);

  const [permissions, setPermissions] = useState<PermissionSettingsView[]>([]);

  const [filters, setFilters] = useState<PermissionViewFilters>({
    text: '',
    limit: 25,
  });
  const [pagination, setPagination] = useState<Pagination>({
    limit: filters.limit ?? 25,
    offset: 0,
  });

  useAsync(async () => {
    const identity = await identityApi.getCredentials();

    const response = await fetchApi.fetch(
      `${await discoveryApi.getBaseUrl('rbac')}/permissionandsubjects?offset=${
        pagination.offset
      }&limit=${pagination.limit}&searchKeyword=${filters.text}`,
      {
        headers: identity.token
          ? { authorization: `Bearer ${identity.token}` }
          : {},
      },
    );

    const data = await response.json();

    const enrichedPermissions = data.permissions.map((permission: any) => {
      const witboostId = transformUrnToWitboostId(permission.entity_ref ?? '');
      const entityRef = witboostId ? parseEntityRef(witboostId) : undefined;
      return { transformed_entity_ref: entityRef?.name ?? '', ...permission };
    });

    const entitiesFilters: { 'metadata.name': string }[] = (
      [
        ...new Set(
          enrichedPermissions.map(
            (role: PermissionSettingsView) => role.transformed_entity_ref,
          ),
        ),
      ] as string[]
    ).map((entityRef: string) => ({
      'metadata.name': entityRef,
    }));

    // holds a map of entity name and its display name
    const displayNamesMap = await getEntitiesDisplayNames(
      catalogApi,
      entitiesFilters,
      {
        chunkLimit: 50,
      },
    );

    const userFriendlyPermissions = enrichedPermissions.flatMap(
      (permission: PermissionSettingsView) => {
        return displayNamesMap.has(
          permission.transformed_entity_ref?.toLowerCase(),
        )
          ? [
              {
                ...permission,
                entity_ref_displayname:
                  displayNamesMap.get(permission.transformed_entity_ref) ??
                  permission.transformed_entity_ref,
              } as PermissionSettingsView,
            ]
          : [
              {
                ...permission,
                entity_ref_displayname: permission.transformed_entity_ref ?? '',
                warning: permission.transformed_entity_ref !== '',
              } as PermissionSettingsView,
            ];
      },
    );

    setPermissions(userFriendlyPermissions);
    setPermissionsTotal((data.total as number) ?? 0);
    setLoading(false);
  }, [
    catalogApi,
    discoveryApi,
    identityApi,
    pagination.limit,
    pagination.offset,
    filters.text,
  ]);

  const reloadTable = useCallback(() => {
    setPagination({ ...pagination, offset: 0 });
  }, [setPagination, pagination]);

  const contextValue = useMemo(
    () => ({
      loading,
      filters,
      changeFilters: <K extends keyof PermissionViewFilters>(
        key: K,
        filterValue: PermissionViewFilters[K],
      ) => {
        setPagination(p => ({ ...p, offset: 0 }));
        setFilters(f => ({ ...f, [key]: filterValue }));
      },
      pagination,
      setPagination,
      permissions,
      permissionsCount: permissionsTotal,
      reloadTable,
    }),
    [loading, filters, permissions, pagination, permissionsTotal, reloadTable],
  );

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissionContext = () => useContext(PermissionContext);
